import React, { useState } from 'react';
import {
  BILLING_INTERVALS,
  CUSTOM_INTERVAL,
  CURRENCY_OPTIONS
} from './planIntervalConstants';
import { formatIntervalLabel, formatMoney } from './planFormatters';
import { getStripePriceStatus } from './planStripeStatus';
import StripeIdField from './StripeIdField';

function isKnownInterval(interval) {
  return BILLING_INTERVALS.some((item) => item.value === interval);
}

export default function PlanPriceRow({
  price,
  onChange,
  onSave,
  onRemove,
  saving = false,
  isDraft = false
}) {
  const [useCustomInterval, setUseCustomInterval] = useState(
    price.interval && !isKnownInterval(price.interval)
  );

  const intervalSelectValue = useCustomInterval ? CUSTOM_INTERVAL : (price.interval || 'month');
  const stripeStatus = getStripePriceStatus(price);
  const isPaid = Number(price.price) > 0;

  const handleIntervalSelect = (value) => {
    if (value === CUSTOM_INTERVAL) {
      setUseCustomInterval(true);
      onChange({ ...price, interval: price.customInterval || '' });
      return;
    }
    setUseCustomInterval(false);
    onChange({ ...price, interval: value });
  };

  const handleCustomInterval = (value) => {
    const slug = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    onChange({ ...price, interval: slug, customInterval: slug });
  };

  const formatUpdatedAt = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return null;
    }
  };

  const lastUpdated = formatUpdatedAt(price.updatedAt);

  return (
    <div className={`sp-price-block ${!price.isActive ? 'sp-price-block-inactive' : ''}`}>
      <div className="sp-price-block-header">
        <div className="sp-price-block-title">
          <strong>{price.code || (isDraft ? 'New price' : 'Price')}</strong>
          {price.id && (
            <span className="sp-price-checkout-id" title="Used as subscriptionPlanId in checkout API">
              Checkout ID: {price.id}
            </span>
          )}
        </div>
        <div className="sp-price-block-badges">
          {price.interval && (
            <span className={`sp-price-interval ${price.interval}`}>
              {formatIntervalLabel(price.interval)}
            </span>
          )}
          <span className={`sp-stripe-status sp-stripe-status-${stripeStatus.status}`}>
            <i className={`fa-solid ${stripeStatus.icon}`} />
            {stripeStatus.label}
          </span>
          {!price.isActive && (
            <span className="sp-badge sp-badge-inactive">Inactive</span>
          )}
        </div>
      </div>

      <div className="sp-price-section">
        <h5 className="sp-price-section-title">
          <i className="fa-solid fa-tag" /> Price details
        </h5>
        <div className="sp-form-grid-2">
          <div className="sp-form-row">
            <label>Code</label>
            <input
              value={price.code || ''}
              onChange={(e) => onChange({ ...price, code: e.target.value.toUpperCase() })}
              placeholder="e.g. SINGLE_MONTHLY"
            />
          </div>
          <div className="sp-form-row">
            <label>Interval</label>
            <select
              value={intervalSelectValue}
              onChange={(e) => handleIntervalSelect(e.target.value)}
            >
              {BILLING_INTERVALS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
              <option value={CUSTOM_INTERVAL}>Custom</option>
            </select>
          </div>
        </div>

        {useCustomInterval && (
          <div className="sp-form-row">
            <label>Custom interval slug</label>
            <input
              value={price.customInterval || price.interval || ''}
              onChange={(e) => handleCustomInterval(e.target.value)}
              placeholder="e.g. biweekly"
            />
          </div>
        )}

        <div className="sp-form-grid-2">
          <div className="sp-form-row">
            <label>Price</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price.price ?? ''}
              onChange={(e) => onChange({ ...price, price: e.target.value })}
            />
          </div>
          <div className="sp-form-row">
            <label>Currency</label>
            <select
              value={price.currency || 'USD'}
              onChange={(e) => onChange({ ...price, currency: e.target.value })}
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="sp-form-row">
            <label>Display order</label>
            <input
              type="number"
              value={price.displayOrder ?? 0}
              onChange={(e) => onChange({ ...price, displayOrder: e.target.value })}
            />
          </div>
          <div className="sp-form-row">
            <label>Customer sees</label>
            <input
              readOnly
              className="sp-readonly-highlight"
              value={price.price !== '' && price.price != null
                ? `${formatMoney(price.price, price.currency)}/${formatIntervalLabel(price.interval || 'month')}`
                : '—'}
            />
          </div>
        </div>
      </div>

      <div className={`sp-price-section sp-stripe-section sp-stripe-section-${stripeStatus.status}`}>
        <h5 className="sp-price-section-title">
          <i className="fa-brands fa-stripe-s" /> Stripe billing
          <span className="sp-stripe-auto-badge">Auto-managed</span>
        </h5>
        <p className="sp-stripe-section-hint">{stripeStatus.hint}</p>

        {isPaid ? (
          <>
            <div className="sp-form-grid-2">
              <StripeIdField
                label="Stripe product ID"
                value={price.stripeProductId}
                hint="One product per plan tier. Shared across monthly/annual prices."
                linkType="product"
              />
              <StripeIdField
                label="Stripe price ID"
                value={price.stripePriceId}
                hint="Unique per billing interval. Used when creating Checkout sessions."
              />
            </div>
            {price.previousStripePriceId && (
              <div className="sp-stripe-archived-note">
                <i className="fa-solid fa-box-archive" />
                Previous price archived: <code>{price.previousStripePriceId}</code>
              </div>
            )}
          </>
        ) : (
          <div className="sp-stripe-free-note">
            <i className="fa-solid fa-circle-info" />
            Free plans skip Stripe. Users get instant activation via the checkout API (<code>mode: free</code>).
          </div>
        )}

        {lastUpdated && !isDraft && (
          <p className="sp-stripe-updated-at">
            <i className="fa-solid fa-clock" /> Last saved: {lastUpdated}
          </p>
        )}
      </div>

      <div className="sp-price-row-footer">
        <label className="sp-toggle">
          <input
            type="checkbox"
            checked={price.isActive !== false}
            onChange={(e) => onChange({ ...price, isActive: e.target.checked })}
          />
          Active on billing page
        </label>
        <div className="sp-price-row-actions">
          {onSave && (
            <button
              type="button"
              className="sp-btn sp-btn-secondary sp-btn-sm"
              disabled={saving}
              onClick={onSave}
            >
              <i className="fa-solid fa-floppy-disk" />
              {saving
                ? 'Saving & syncing...'
                : isDraft
                  ? 'Add price'
                  : isPaid
                    ? 'Save & sync to Stripe'
                    : 'Save price'}
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              className="sp-btn sp-btn-ghost sp-btn-sm"
              disabled={saving}
              onClick={onRemove}
            >
              <i className="fa-solid fa-trash" />
              {isDraft ? 'Discard' : 'Deactivate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
